import { useContext, useEffect, useState } from "react";
import { AlertCircle, Download, X } from "lucide-react";

import Field from "../field";
import {
  COMMENT_POLICY_ENUM,
  REVIEW_STATUS_ENUM,
  SERVER_ORIGIN,
} from "../consts";
import { Context } from "../context";

/* ---------------------------------------------------------------------- */
/*  Форма проверки работы студента преподавателем                         */
/* ---------------------------------------------------------------------- */

export default function WorkReview({
  isOverdue,
  codeFileName,
  codeLink,
  reportFileName,
  reportLink,
  savedReviewStatus,
  onCancel,
  onUpdateStatus,
  onAddComment,
}) {
  const [reviewStatus, setReviewStatus] = useState();
  const [comment, setComment] = useState("");
  const [errors, setErrors] = useState([]);

  const isStatusDisabled =
    savedReviewStatus?.code === REVIEW_STATUS_ENUM.notSubmitted;

  const filterFunc = isOverdue
    ? (elem) => !elem.system && elem.code !== REVIEW_STATUS_ENUM.rework
    : (elem) => !elem.system;

  const store = useContext(Context);
  const reviewStatuses = store?.reviewStatuses?.filter(filterFunc);
  const maxCommentLength = store?.maxCommentLength;

  const isCommentForbidden =
    reviewStatus?.commentPolicy === COMMENT_POLICY_ENUM.forbidden;

  const isCommentRequired =
    reviewStatus?.commentPolicy === COMMENT_POLICY_ENUM.required;

  useEffect(() => {
    if (savedReviewStatus?.code) {
      const res = reviewStatuses.find(
        (elem) => elem.code === savedReviewStatus.code,
      );
      if (res) {
        setReviewStatus(res);
      }
    }
  }, [savedReviewStatus?.code]);

  const validate = () => {
    const errs = [];
    if (!reviewStatus) errs.push("Выберите статус.");
    if (isCommentRequired && !comment) errs.push("Напишите комментарий.");
    return errs;
  };

  const updateStatus = () => {
    const errs = validate();
    setErrors(errs);
    if (errs.length) return;

    const body = {
      statusCode: reviewStatus.code,
    };
    if (!isCommentForbidden) {
      body.comment = comment;
    }
    onUpdateStatus(body);
  };

  const addComment = () => {
    onAddComment({ text: comment });
  };

  const renderLink = (pathName, label) => {
    if (pathName) {
      return (
        <a className="link" href={`${SERVER_ORIGIN}${pathName}`}>
          <Download size={12} /> {label}
        </a>
      );
    }
    return <span className="muted small">не размещён</span>;
  };

  const renderAttachment = (fileName, link, label) => (
    <div className="attachment-container">
      <span className="muted small">{label}</span>
      {renderLink(link, fileName)}
    </div>
  );

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true">
      <div className="modal">
        <div className="modal-head">
          <h2>Проверка работы</h2>
          <button className="icon-btn" onClick={onCancel} aria-label="Закрыть">
            <X size={18} />
          </button>
        </div>

        <div className="modal-body">
          <div className="attachments">
            {renderAttachment(reportFileName, reportLink, "Отчёт:")}
            {renderAttachment(codeFileName, codeLink, "Исходный код:")}
          </div>
          {isOverdue && (
            <p className="warning small">
              Период доработки завершён. Для продолжения работы перенесите дату
              завершения практической работы
            </p>
          )}
          <div className="check-status">
            <Field label="Статус" required>
              <select
                disabled={isStatusDisabled}
                value={reviewStatus?.code}
                onChange={(e) => {
                  const selected = reviewStatuses.find(
                    (elem) => elem.code === e.target.value,
                  );
                  setReviewStatus(selected);
                }}
              >
                <option>Выберите статус</option>
                {reviewStatuses.map((s) => (
                  <option
                    key={s.code}
                    value={s.code}
                    style={{ color: s.color }}
                  >
                    {s.name}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <label className="field">
            <span className="field-label">
              Комментарий
              {isCommentRequired ? <span className="req"> *</span> : null}
            </span>
            <textarea
              className="comment-input"
              disabled={isCommentForbidden}
              maxLength={maxCommentLength}
              name="comment"
              rows={5}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            ></textarea>
            <p className="muted small">{`Символов: ${comment.length} / ${maxCommentLength}`}</p>
          </label>

          {errors.length > 0 && (
            <div className="error-box">
              {errors.map((e, i) => (
                <div key={i}>
                  <AlertCircle size={14} /> {e}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="modal-foot">
          <button className="btn ghost" onClick={onCancel}>
            Отмена
          </button>
          <button
            className="btn ghost"
            disabled={isCommentForbidden}
            onClick={addComment}
          >
            Добавить комментарий
          </button>
          <button
            className="btn primary"
            disabled={isStatusDisabled}
            onClick={updateStatus}
          >
            Изменить статус
          </button>
        </div>
      </div>
    </div>
  );
}
